import type { MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import { AssignedAdminsStack } from '../../../../components/AssignedAdminsStack';
import { PatientAvatarImage } from '../../../../components/PatientAvatarImage';
import { StatusChip } from '../../../../components/StatusChip';
import type { Patient, StageKind } from '../../../../types/admin';
import styles from './funnel.module.scss';

type FunnelPatientCardProps = {
  readonly patient: Patient;
  readonly stageKind: StageKind;
  readonly onOpen: () => void;
  readonly onContextMenu: (event: ReactMouseEvent) => void;
};

export function FunnelPatientCard({ patient, stageKind, onOpen, onContextMenu }: FunnelPatientCardProps) {
  const { t } = useTranslation();
  // Cards without a cycle cannot be moved (no cycle to PATCH); keep them static.
  const draggable = patient.current_cycle_number != null;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: patient.id,
    data: { patient, fromStage: stageKind },
    disabled: !draggable,
  });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  const fullName = `${patient.first_name} ${patient.last_name}`.trim();
  const meta = patient.display_id ?? patient.registration_code ?? t('clients.noClientId');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.card}
      data-dragging={isDragging || undefined}
      onClick={onOpen}
      onContextMenu={onContextMenu}
      {...attributes}
      {...listeners}
    >
      <div className={styles.cardTop}>
        <div className={styles.cardAvatar}>
          {patient.avatar_url ? (
            <PatientAvatarImage
              patientId={patient.id}
              avatarUrl={patient.avatar_url}
              fallback={
                <>
                  {patient.first_name[0]}
                  {patient.last_name[0]}
                </>
              }
            />
          ) : (
            <>
              {patient.first_name[0]}
              {patient.last_name[0]}
            </>
          )}
        </div>
        <div className={styles.cardText}>
          <span className={styles.cardName}>{fullName}</span>
          <span className={styles.cardMeta}>{meta}</span>
        </div>
      </div>
      <div className={styles.cardBottom}>
        {patient.active_treatment_status ? (
          <StatusChip status={patient.active_treatment_status} />
        ) : (
          <span />
        )}
        <AssignedAdminsStack admins={patient.assigned_admins ?? []} />
      </div>
    </div>
  );
}
